import { ApolloCache } from '@apollo/client';
import { LanguageContext } from 'lib-knowledge-management-common/contexts/language';
import {
  LanguageId,
  OrganizationSettingsDocument,
  OrganizationSettingsQuery,
  useOrganizationEnableLanguagesMutation,
} from 'lib-knowledge-management-common/graphql';
import { LanguageModel } from 'lib-knowledge-management-common/hooks/useManagementLanguages';
import { ItemType } from 'lib-knowledge-utils/components/itemType';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import { useContext } from 'react';

type UseHandleLanguageSelectionReturn = {
  handleLanguageSelection: (item: ItemType) => void;
  inactiveLanguages: LanguageModel[];
};

export function useHandleLanguageSelection(): UseHandleLanguageSelectionReturn {
  const { availableLanguages, enabledLanguages } = useContext(LanguageContext);

  const inactiveLanguages = availableLanguages.filter(
    (l) => !enabledLanguages.find((e) => e.languageId === l.languageId),
  );

  const [enableLanguage] = useOrganizationEnableLanguagesMutation();
  const { successNotify, errorNotify } = useNotification();

  async function handleLanguageSelection(item: ItemType) {
    if (!item.id) return;
    try {
      await enableLanguage({
        variables: {
          languageIds: [item.id.toString() as LanguageId],
        },
        update: (cache: ApolloCache<unknown>, { data }) => {
          const currentOrgSettings = cache.readQuery<OrganizationSettingsQuery>(
            {
              query: OrganizationSettingsDocument,
            },
          );

          if (currentOrgSettings && data) {
            const newLanguage =
              data.organisationEnableLanguages.enabledLanguages.find(
                (lang) => lang.languageId === item.id,
              );

            if (newLanguage) {
              const updatedEnabledLanguages = [
                ...currentOrgSettings.organisationSettings.enabledLanguages,
                newLanguage,
              ].sort((a, b) => (a.languageId > b.languageId ? 1 : -1));

              cache.writeQuery<OrganizationSettingsQuery>({
                query: OrganizationSettingsDocument,
                data: {
                  ...currentOrgSettings,
                  organisationSettings: {
                    ...currentOrgSettings.organisationSettings,
                    enabledLanguages: updatedEnabledLanguages,
                  },
                },
              });
            }
          }
        },
      });
      successNotify({ text: 'Language enabled', title: 'Success' });
    } catch (_error) {
      errorNotify({
        text: 'Language could not be enabled, please refresh and try again',
        time: 10000,
      });
    }
  }

  return {
    handleLanguageSelection,
    inactiveLanguages,
  };
}
